import type { BeforeSendFn, CaptureResult, Properties } from 'posthog-js';

/**
 * URL クエリから除去する機微パラメータ。
 *
 * 認証コールバック（集英社IDの `?code=`）やワンタイム認証・メール認証などで
 * 認証マテリアルが URL に載るため、`$current_url` 等で PostHog に送らない（GF-1889 / セキュリティ F1）。
 */
const SENSITIVE_QUERY_KEYS = [
  'code',
  'token',
  'state',
  'hash',
  'codeChallenge',
  'code_challenge',
  'auth_key',
  'email',
  'password',
];

/** 機微値を伏せた後に入る目印。値自体は送らないがパラメータの存在は分析で分かるようにする。 */
const REDACTED = 'redacted';

/**
 * URL 文字列の機微クエリ値を redacted に置換する。
 * 絶対URL以外（相対パス / `$direct` 等）は解析できないためそのまま返す。
 */
export const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    let changed = false;
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, REDACTED);
        changed = true;
      }
    }
    return changed ? parsed.toString() : url;
  } catch {
    return url;
  }
};

/**
 * URL を値に持つ PostHog プロパティ。これらのクエリから機微値を除去する。
 *
 * `$initial_*` は初回タッチ時に person プロパティ（`$set_once`）へ格納されるため、
 * 初回アクセスが認証コールバックだと認証コードがここに残る。必ず対象に含める。
 */
const URL_PROPERTY_KEYS = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'];

/** properties / $set / $set_once いずれの入れ物でも、URL 系キーの機微クエリを除去する。 */
const sanitizeProperties = (properties: Properties | undefined): void => {
  if (!properties) return;
  for (const key of URL_PROPERTY_KEYS) {
    const value = properties[key];
    if (typeof value === 'string') {
      properties[key] = sanitizeUrl(value);
    }
  }
};

/**
 * `posthog.init({ before_send })` 用。全イベント送信前に URL 系プロパティの機微クエリを除去する。
 *
 * - 認証コード等が `$current_url` / `$referrer`（イベント）や
 *   `$initial_current_url` / `$initial_referrer`（`$set` / `$set_once` の person プロパティ）
 *   経由で PostHog に渡るのを防ぐ
 * - イベント自体は破棄せず（分析を壊さない）、値だけ redacted 化する
 */
export const sanitizePostHogEvent: BeforeSendFn = (event: CaptureResult | null) => {
  if (!event) return event;
  sanitizeProperties(event.properties);
  sanitizeProperties(event.$set);
  sanitizeProperties(event.$set_once);
  return event;
};
